/**
 * env_carry — THE LATCH LAW (lighting 3b, 2026-09-27): how `scene.environmentIntensity` carries the sky between two
 * environment bakes, and when a bake is due. Pure (no three); `verify:lighting-physical` pins every rule.
 *
 * THE CUBE. The engine bakes the sky into a PMREM cube over ~15 frames (six faces, the base, the GGX levels) and
 * LATCHES the bake values at the first face; `environmentIntensity` then carries the sky's change until the next bake
 * as a scalar — the "scale" `rs × (diffuse lux + the night floor)` now over the same product at the latch.
 *
 * MEASURED (`verify/sky-env-probe.mjs`, WebGL2 + WebGPU potato, 2026-09-27, every frame traced):
 *   - A graphics APPLY does not move the carrier: four `__gfx.setMany` applies at a pinned noon, env 1.000 on every
 *     frame of both backends, no bake started, the exposure, fog and the cube's own luminance unchanged. (The ground
 *     agent's 0.083 → 0.00018 swing is not reproducible from applies; its class — a cube from another sky — is.)
 *   - A PIN latches a TRANSIENT: the time jumps one frame, the snapped weather lands the next; the sun-drift bake
 *     latched the frame between (sunset under a clear sky; noon under the storm), completed 15 frames later, and only
 *     then did the right cycle run — 30 stale frames per jump (30 s under SwiftShader).
 *   - A RELOAD starts from the server's hour: the boot bake latched NIGHT (rs 196,010, diffuse 0 — a black cube), the
 *     pinned noon then read env ×9.44 for 16–20 frames; and while the next cycle's PMREM stage rewrote the LIVE target
 *     level by level, its sharp levels already held the noon sky under the night latch's ×9.44 — the whitened sea.
 *
 * THE RULES:
 *   1. Latch from a SETTLED frame: a new cycle starts only after `SETTLE_FRAMES` frames with the sky's signature still
 *      (`skyChanged`); the engine overrules a gate that refuses `ENV_BAKE_GATE_MAX_DEFER` frames in a row.
 *   2. A cube of another REGIME (outside ×`ENV_REGIME`, `REGIME_SUN_DEG` of sun, `REGIME_CLOUD` of overcast) or a
 *      world entry / reload / space return is re-baked AT ONCE in one frame (`immediate` — atomic: the one-shot latches
 *      and completes together); a cube merely stale (`ENV_BAND`, `BAND_SUN_DEG`, `BAND_CLOUD`) gets a staged cycle; a
 *      cycle in flight whose latch went stale is restarted in its face stage (a regime-stale one past it is replaced
 *      by the one-shot, a band-stale one finishes — ≤ 9 frames — and the next follows).
 *   3. The carrier is BOUNDED (`ENV_MIN`–`ENV_MAX`): outside it the cube is the wrong sky and a scalar cannot fix it.
 *   4. The IBL's effective brightness (the carrier × the cube's scale) SLEWS at ≤ `ENV_SLEW_STOPS_PER_S`, so a new
 *      cube landing never moves the frame (the carrier re-normalizes in the same frame); a regime jump — which the key
 *      light and the dome take in one frame — snaps with them.
 */
import { NIGHT_FLOOR_LUX } from '../photometry.js';

/** The sky a frame is lit under: the dome's radiance scale (E0 × luxToScene), the diffuse lux, the overcast weight, sin(sun elevation). */
export interface SkySig { rs: number; dif: number; cloud: number; sunY: number }
/** What a cube was baked under (the engine's `SkyBakeOverrides`: radianceScale, tag = the diffuse lux, sunY, cloud). */
export interface CubeSig { rs: number; tag: number; sunY?: number; cloud?: number }

/** The carrier's authority over a cube baked under another sky. */
export const ENV_MIN = 0.25;
export const ENV_MAX = 4;
/** A staged re-bake once the cube is this far from now (scale ratio), or this much sun / overcast apart. */
export const ENV_BAND = 1.25;
export const BAND_SUN_DEG = 2;
export const BAND_CLOUD = 0.1;
/** A one-shot re-bake past these: the cube is another regime. */
export const ENV_REGIME = 4;
export const REGIME_SUN_DEG = 8;
export const REGIME_CLOUD = 0.3;
/** Frames the signature must hold before a cycle may latch. */
export const SETTLE_FRAMES = 2;
/** The IBL's effective brightness moves at most this many stops a second (a regime jump snaps). */
export const ENV_SLEW_STOPS_PER_S = 4;
/** One-shot bakes of the SAME sky are at least this many frames apart (a guard against a loop that never settles the ratio);
 *  a sky that moved again since the last one-shot (a world entry, then the probe's pin three frames later) is baked at once. */
export const IMMEDIATE_GAP_FRAMES = 12;
/**
 * Past astronomical twilight the sky is black and static (the engine's DARK_SKY_SUN_Y, which also stretches its bake
 * interval ×8 there): the sun's angle between two night cubes is no regime change — or every 8° of a night would buy a
 * one-shot hitch the dark-sky damping exists to avoid.
 */
export const DARK_SUN_Y = -0.35;
/** The per-frame stillness a settled frame needs (log ratio of rs and diffuse; overcast weight; sin elevation). */
export const SETTLE_LOG = 0.02;
export const SETTLE_CLOUD = 0.01;
export const SETTLE_SUN_Y = 0.002;

const finite = (v: number | undefined): v is number => typeof v === 'number' && Number.isFinite(v);

/** The IBL brightness proxy: the dome's radiance scale × the diffuse lux (+ the night floor, so a dark sky never divides by ~0). */
export function envScale(rs: number, dif: number): number {
  return Math.max(0, rs) * (Math.max(0, finite(dif) ? dif : 0) + NIGHT_FLOOR_LUX);
}

/** A cube's scale, or null when the latch carries no tag (an old bake / none yet). */
export function cubeScaleOf(c: CubeSig | null): number | null {
  if (!c || !(c.rs > 0) || !finite(c.tag)) return null;
  return envScale(c.rs, c.tag);
}

/** True when the sky moved this frame beyond the settle thresholds. */
export function skyChanged(a: SkySig, b: SkySig): boolean {
  const lr = Math.abs(Math.log(Math.max(1e-12, a.rs) / Math.max(1e-12, b.rs)));
  const ld = Math.abs(Math.log((Math.max(0, a.dif) + NIGHT_FLOOR_LUX) / (Math.max(0, b.dif) + NIGHT_FLOOR_LUX)));
  return !(lr <= SETTLE_LOG) || !(ld <= SETTLE_LOG) || !(Math.abs(a.cloud - b.cloud) <= SETTLE_CLOUD) || !(Math.abs(a.sunY - b.sunY) <= SETTLE_SUN_Y);
}

/** Sun elevation in degrees from its sine. */
export function elevDeg(sunY: number): number {
  return (Math.asin(Math.max(-1, Math.min(1, sunY))) * 180) / Math.PI;
}

export type CubeVerdict = 'fresh' | 'band' | 'regime';

/** How far a cube is from the sky now: fresh (carry it), band (re-bake, staged), regime (re-bake at once). No cube = regime. */
export function cubeVerdict(now: SkySig, cube: CubeSig | null): CubeVerdict {
  const cs = cubeScaleOf(cube);
  if (cs === null || !cube) return 'regime';
  const ratio = envScale(now.rs, now.dif) / Math.max(1e-30, cs);
  const bothDark = finite(cube.sunY) && now.sunY < DARK_SUN_Y && cube.sunY < DARK_SUN_Y;
  const dEl = finite(cube.sunY) && !bothDark ? Math.abs(elevDeg(now.sunY) - elevDeg(cube.sunY)) : 0;
  const dCl = finite(cube.cloud) ? Math.abs(now.cloud - cube.cloud) : 0;
  if (!(ratio <= ENV_REGIME && ratio >= 1 / ENV_REGIME) || dEl > REGIME_SUN_DEG || dCl > REGIME_CLOUD) return 'regime';
  if (!(ratio <= ENV_BAND && ratio >= 1 / ENV_BAND) || dEl > BAND_SUN_DEG || dCl > BAND_CLOUD) return 'band';
  return 'fresh';
}

/** The effective scale one frame on: toward `target` at ≤ ENV_SLEW_STOPS_PER_S × dt stops; no history → the target. */
export function slewScale(prev: number | null, target: number, dtS: number): number {
  if (prev === null || !(prev > 0) || !(target > 0)) return target;
  const maxStops = ENV_SLEW_STOPS_PER_S * Math.max(0, finite(dtS) ? dtS : 0);
  const d = Math.log2(target / prev);
  if (Math.abs(d) <= maxStops) return target;
  return prev * Math.pow(2, Math.sign(d) * maxStops);
}

/** The carrier the materials read: the effective scale over the cube's, bounded; no cube → 1. */
export function envIntensityOf(scale: number, cubeScale: number | null): number {
  if (cubeScale === null || !(cubeScale > 0) || !finite(scale)) return 1;
  return Math.min(ENV_MAX, Math.max(ENV_MIN, scale / cubeScale));
}

export interface BakePolicyIn {
  /** The signature held for SETTLE_FRAMES. */
  settled: boolean;
  /** The completed cube against now. */
  verdict: CubeVerdict;
  /** The cycle in flight (its step and its latch against now), or null. */
  inFlight: { step: number; verdict: CubeVerdict } | null;
  /** A world entry / reload / space return has not had its bake of the current frame yet. */
  entryPending: boolean;
  framesSinceImmediate: number;
  /** The sky left the last one-shot's latch (its verdict against now is not fresh) — or there was none. */
  movedSinceImmediate: boolean;
}
export type BakeAction = 'none' | 'immediate' | 'restart' | 'request';

/** Which bake, if any, this frame asks for (rule 2). Nothing latches from an unsettled frame. */
export function bakeAction(i: BakePolicyIn): BakeAction {
  if (!i.settled) return 'none';
  const canImmediate = i.framesSinceImmediate >= IMMEDIATE_GAP_FRAMES || i.movedSinceImmediate;
  if ((i.entryPending || i.verdict === 'regime') && canImmediate) return 'immediate';
  if (i.inFlight) {
    if (i.inFlight.verdict === 'fresh') return 'none';
    if (i.inFlight.step < 6) return 'restart';
    return i.inFlight.verdict === 'regime' && canImmediate ? 'immediate' : 'none';
  }
  return i.verdict === 'fresh' ? 'none' : 'request';
}
